/* =============================================================
   APP CONFIG
   =============================================================
   THE config file for product-level facts: names, URLs, copy that
   appears in the chrome, storage keys, and behavioural knobs.

   Nothing in `src/components`, `src/layouts` or `src/pages` should
   hard-code a product name, a domain or a route string — it should
   read it from here (or from `routes.ts` / `nav.config.ts`).

   Values can be overridden per-deployment with VITE_* env vars, so
   a white-label instance is a config change, not a fork.
   ============================================================= */

import { env } from './env'

export const appConfig = {
  /** Product name, shown in the sidebar, auth screens and <title>. */
  name: env.appName || 'River Atlas',

  /** Legal entity behind the deployment — footer / auth small print. */
  org: env.appOrg || 'River Atlas',

  /** One-liner used on the sign-in / sign-up brand panel. */
  tagline: 'Inland waterway infrastructure, in one place.',
  description:
    'Register docking infrastructure, list your vessels, and search, compare and book berths across Europe.',

  /** Where the product lives. */
  urls: {
    marketing: env.marketingUrl || 'https://riveratlas.eu',
    app: 'https://app.riveratlas.eu',
    api: 'https://api.riveratlas.eu',
    terms: 'https://riveratlas.eu/terms',
    privacy: 'https://riveratlas.eu/privacy',
  },

  supportEmail: env.supportEmail || 'hello@riveratlas.eu',

  /** Browser-title pattern. `%s` is the current page title. */
  titleTemplate: '%s · River Atlas',

  /** localStorage keys. Namespaced so several apps can share an origin. */
  storageKeys: {
    session: 'river-atlas.session',
    sidebarCollapsed: 'river-atlas.sidebar-collapsed',
  },

  /** UI behaviour knobs. */
  ui: {
    /** Below this width the sidebar becomes an overlay drawer (px, matches CSS). */
    mobileBreakpoint: 900,
    /** Sidebar starts collapsed to icons on first visit? */
    sidebarCollapsedByDefault: false,
    /** Minimum password length enforced on the sign-up form. */
    minPasswordLength: 8,
  },

  /**
   * Account types, from `docs/data-model.md` → User.role.
   * Drives the sign-up role picker and (later) nav visibility.
   */
  roles: [
    {
      value: 'infrastructure_owner',
      label: 'Infrastructure owner',
      hint: 'I manage quays, berths or docking locations.',
      icon: 'anchor',
    },
    {
      value: 'shipowner',
      label: 'Shipowner / operator',
      hint: 'I operate vessels and need to book berths.',
      icon: 'ship',
    },
    {
      value: 'both',
      label: 'Both',
      hint: 'My organisation does both.',
      icon: 'layers',
    },
  ],
} as const

export type RoleOption = (typeof appConfig.roles)[number]
